import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Alert, AlertDescription } from "../../components/ui/alert"
import { Mail, CheckCircle, XCircle, Loader2, ArrowLeft } from "lucide-react"
import { apiService } from "../../lib/api"
import { formatErrorMessage } from "../../lib/utils"

// Brand colors from landing page and my-account
const BRAND_COLORS = {
  primary: '#c39d76', // Warm beige/tan
  secondary: '#d4a574', // Lighter tan
  dark: '#2C2416', // Dark brown
  darkGrey: '#3F454A', // Dark grey
  accent: '#dfc9b1', // Light beige
}

export default function VerifyEmail() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error' | 'pending'>('pending')
  const [message, setMessage] = useState('')
  const [isResending, setIsResending] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [hasAttemptedVerification, setHasAttemptedVerification] = useState(false)
  
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    // If token is provided, verify immediately (but only once)
    if (token && email && !hasAttemptedVerification) {
      setHasAttemptedVerification(true)
      verifyEmail()
    }
  }, [token, email, hasAttemptedVerification])

  // Countdown effect for redirect
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0 && status === 'success') {
      navigate('/login')
    }
  }, [countdown, status, navigate])

  const verifyEmail = async () => {
    // Prevent duplicate calls
    if (status === 'verifying') {
      return;
    }
    
    if (!token || !email) {
      setStatus('error')
      setMessage('Invalid verification link. Please check your email and try again.')
      return
    }

    setStatus('verifying')
    try {
      const response = await apiService.verifyEmail(token, email)
      setStatus('success')
      
      // Handle different success scenarios
      if (response.alreadyVerified) {
        setMessage('✅ Your email is already verified! You can log in now.')
      } else {
        setMessage('🎉 Email verified successfully! Welcome to IKORO. You can now log in.')
      }
      
      // Start countdown and redirect
      setCountdown(5)
    } catch (err: any) {
      setStatus('error')
      
      // Handle specific error cases with user-friendly messages
      if (err?.response?.data?.error?.code === 'token_expired') {
        setMessage('⏰ Your verification link has expired. Don\'t worry - we can send you a new one!')
      } else if (err?.response?.data?.error?.code === 'invalid_token') {
        setMessage('🔗 This verification link is invalid. Please check your email for the correct link or request a new one.')
      } else if (err?.response?.data?.alreadyVerified) {
        setStatus('success')
        setMessage('✅ Your email is already verified! You can log in now.')
        setCountdown(5)
      } else if (err?.response?.data?.error?.code === 'invalid_request') {
        setMessage('❌ Invalid verification request. Please check your email and click the verification link again.')
      } else {
        setMessage('❌ Something went wrong during verification. Please try clicking the link again or request a new verification email.')
      }
    }
  }

  const resendVerification = async () => {
    if (!email) {
      setMessage('❌ Email address is required to resend verification.')
      return
    }

    setIsResending(true)
    try {
      const response = await apiService.resendVerificationEmail(email)
      
      if (response.alreadyVerified) {
        setStatus('success')
        setMessage('✅ Your email is already verified! You can log in now.')
        setCountdown(3)
      } else {
        setMessage('📧 New verification email sent! Please check your inbox and spam folder.')
        setStatus('pending')
      }
    } catch (err: any) {
      console.error('Resend failed:', err)
      
      // Handle specific error cases
      if (err?.response?.data?.error?.code === 'user_not_found') {
        setMessage('❌ No account found with this email address. Please check the email or register a new account.')
      } else if (err?.response?.data?.alreadyVerified) {
        setStatus('success')
        setMessage('✅ Your email is already verified! You can log in now.')
        setCountdown(3)
      } else {
        setMessage('❌ Failed to send verification email. Please try again in a few moments.')
      }
    } finally {
      setIsResending(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Image with Black Overlay */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: "url(/log-sin-bg.jpg)",
          }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="relative z-10 flex items-center justify-center w-full p-12">
          <div className="text-center text-white space-y-6">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-24 w-24 object-contain mx-auto mb-8"
            />
            <h1 className="text-4xl font-bold">Welcome to IKORO</h1>
            <p className="text-lg text-gray-200">Join us and start your journey with ikoro</p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-white">
        <div className="w-full max-w-md">
        <div className="text-center space-y-2 pb-4">
          {/* Brand Logo and Title in One Row */}
          <div className="flex items-center justify-center gap-3 mb-2">
            <img 
              src="/brand-b.png" 
              alt="Brand Logo" 
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-xl font-bold m-0" style={{ color: BRAND_COLORS.dark }}>
              Verify Email
            </h1>
          </div>
          <p className="text-sm" style={{ color: BRAND_COLORS.darkGrey }}>
            {email ? `Verify your email: ${email}` : 'Please verify your email address'}
          </p>
        </div>

        <div className="space-y-4 px-6 pb-6">
          {status === 'verifying' && (
            <div className="text-center py-8">
              <Loader2 className="h-12 w-12 mx-auto mb-4 animate-spin" style={{ color: BRAND_COLORS.primary }} />
              <p className="text-sm" style={{ color: BRAND_COLORS.dark }}>
                Verifying your email...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center py-8">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{
                background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
              }}>
                <CheckCircle className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-lg font-bold mb-2" style={{ color: BRAND_COLORS.dark }}>
                Email Verified!
              </h2>
              <p className="text-sm mb-4" style={{ color: BRAND_COLORS.darkGrey }}>
                {message}
                {countdown > 0 && (
                  <span className="block mt-2 text-xs opacity-75">
                    Redirecting to login in {countdown} seconds...
                  </span>
                )}
              </p>
              <Button
                onClick={() => navigate('/login')}
                className="w-full font-semibold py-2.5"
                style={{
                  background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                  color: 'white',
                }}
              >
                {countdown > 0 ? `Go to Login (${countdown}s)` : 'Go to Login'}
              </Button>
            </div>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive" className="border-red-500 bg-red-50">
                <XCircle className="h-4 w-4" />
                <AlertDescription className="text-red-600">{message}</AlertDescription>
              </Alert>
              
              {email && (
                <Button
                  onClick={resendVerification}
                  disabled={isResending}
                  className="w-full font-semibold py-2.5"
                  style={{
                    background: `linear-gradient(135deg, ${BRAND_COLORS.secondary} 0%, ${BRAND_COLORS.primary} 100%)`,
                    color: 'white',
                  }}
                >
                  {isResending ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Resend Verification Email
                    </>
                  )}
                </Button>
              )}
            </div>
          )}

          {status === 'pending' && !token && (
            <div className="space-y-4 text-center py-8">
              <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{
                backgroundColor: `${BRAND_COLORS.primary}20`,
              }}>
                <Mail className="w-8 h-8" style={{ color: BRAND_COLORS.primary }} />
              </div>
              <h2 className="text-lg font-bold mb-2" style={{ color: BRAND_COLORS.dark }}>
                Check Your Email
              </h2>
              <p className="text-sm mb-4" style={{ color: BRAND_COLORS.darkGrey }}>
                We've sent a verification link to <strong>{email || 'your email'}</strong>. 
                Please check your inbox and spam folder, then click the link to verify your account.
              </p>
              {message && (
                <Alert className="border-green-500 bg-green-50">
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  <AlertDescription className="text-green-600">{message}</AlertDescription>
                </Alert>
              )}
              {email && (
                <div className="space-y-2">
                  <Button
                    onClick={resendVerification}
                    disabled={isResending}
                    variant="outline"
                    className="w-full font-semibold py-2.5 hover:!text-white hover:!bg-[#c39d76] hover:!border-[#c39d76]"
                    style={{
                      borderColor: BRAND_COLORS.primary,
                      color: BRAND_COLORS.primary,
                    }}
                  >
                    {isResending ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Resend Verification Email
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}

